import React, { Suspense, lazy, useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  RouteObject,
  Outlet,
} from "react-router-dom";
import RouteErrorFallback from "./components/errors/RouteErrorFallback";
import { ROUTES } from "./utils/constants";
import { authLoader } from "./loaders/auth/authLoader";
import { loginLoader } from "./loaders/auth/loginLoader";
import HealthCareListContainer from "./containers/health-care/HealthCareListContainer";
import PreAuthContainer from "./containers/patient/PreAuthContainer";
import { Loader } from "./utils/Spinners"; // Import the Loader component
import FunctionalityBasedRoute from "./utils/FunctionalityBasedRoute";
import DashboardContainer from "./containers/dashboard/DashboardContainer";
import EmailContainer from "./containers/email/EmailContainer";
import DocumentDeficiencyPerPatient from "./containers/document-deficiency/DocumentDeficiencyPerPatient";
import HDRListContainer from "./containers/ohdr/HDRListContainer";
import NewDashboardContainer from "./containers/dashboard/NewDashboardContainer";
import OperationalDashboard from "./components/dashboard/OperationalDashboard";
import CreditRegisterFileData from "./components/creditRegisterUpload/CreditRegisterFileData";
import EODBODScreenContainer from "./containers/patient/EOD-BOD-Screen/EODBODScreenContainer";
import ReminderEODBODModal from "./ReminderEODBODModal";
import { store } from "./store/store";
import { syncSessionCookieFromStorage } from "./utils/sessionCookie";
import { setupBillAuditTokenBridge } from "./utils/billAuditSession";

const AddPatientSchemeContainer = lazy(
  () => import("./containers/patient/AddPatientSchemeContainer"),
);
const PatientCaseDetailsContainer = lazy(
  () => import("./containers/cases/CaseDetailsContainer"),
);
const AuthContainer = lazy(() => import("./containers/auth/AuthContainer"));

const PatientDetailsContainer = lazy(
  () => import("./containers/patient/PatientDetailsContainer"),
);
const PatientAddContainer = lazy(
  () => import("./containers/cases/AddCaseContainer"),
);
const PatientListContainer = lazy(
  () => import("./containers/patient/PatientListContainer")
);

const EmailsContainer = lazy(() => import("./containers/email/EmailContainer"));
const PatientListContainerTabs = lazy(
  () => import("./containers/patient/PatientListContainerTabs"),
);
const LandingPageContainer = lazy(
  () => import("./containers/landing-page/LandingPageContainer"),
);
const ClaimsListContainer = lazy(
  () => import("./containers/claims/ClaimsListContainer"),
);
const CaseDetailsContainer = lazy(
  () => import("./containers/cases/CaseDetailsContainer"),
);
const CaseHistoryContainer = lazy(
  () => import("./containers/cases/CaseHistoryContainer"),
);
const CaseListContainer = lazy(
  () => import("./containers/cases/CaseListContainer"),
);
const VisitListContainer = lazy(
  () => import("./containers/visits/VisitListContainer"),
);
const AddPatientContainer = lazy(
  () => import("./containers/patient/AddPatientContainer"),
);
const HealthCareContainer = lazy(
  () => import("./containers/health-care/AddHealthCareContainer"),
);
const InsuranceSetupContainer = lazy(
  () => import("./containers/insurance-setup/InsuranceSetupContainer"),
);
const AllCasesContainer = lazy(
  () => import("./containers/insurance-setup/AllCasesContainer"),
);
const UserManagementContainer = lazy(
  () => import("./containers/userManagement/UserManagementContainer"),
);
const NewUserManagementContainer = lazy(
  () => import("./containers/userManagement/NewUserManagementContainer"),
);
const TaskAllocationContainer = lazy(
  () => import("./containers/TaskAllocation/TaskAllocationContainer"),
);
const CreateOrEditTaskAllocationContainer = lazy(
  () => import("./containers/TaskAllocation/CreateOrEditTaskAllocationContainer"),
);
const BillsContainer = lazy(() => import("./containers/bills/BillsContainer"));

const AddBillContainer = lazy(
  () => import("./containers/bills/add-bill/AddBillContainer"),
);

const PortalDetailsFetchContainer = lazy(
  () => import("./containers/Portal Details Fetch/PortalDetailsFetchContainer"),
);

const BillItemsReviewContainer = lazy(
  () => import("./containers/bills/bill-items-review/BillItemsReviewContainer"),
);
const GovtBillItemsReviewContainer = lazy(
  () =>
    import("./containers/bills/govt-billing/govt-bill-items-review/GovtBillItemsReviewContainer"),
);
const TPABillAuditContainer = lazy(
  () =>
    import("./containers/bills/tpa-billing/tpa-bill-audit/TPABillAuditContainer"),
);
const GovtBillAuditContainer = lazy(
  () =>
    import("./containers/bills/govt-billing/govt-bill-audit/GovtBillAuditContainer"),
);
const DSBillCompContainer = lazy(
  () => import("./containers/bills/common/ds-bill-comp/DSBillCompContainer"),
);
const PatientBillingListContainer = lazy(
  () => import("./containers/patient/Billing/PatientBillsListContainer"),
);
const IPDBillingContainer = lazy(
  () => import("./containers/ipd-bills/IPDBillsContainer"),
);
const AddNewIPDBillContainer = lazy(
  () => import("./containers/ipd-bills/AddIPDBillContainer"),
);
const EditIPDBillContainer = lazy(
  () => import("./containers/ipd-bills/EditIPDBillContainer"),
);
const OPDBillingContainer = lazy(
  () => import("./containers/opd-bills/OPDBillsContainer"),
);
const AddNewOPDBillContainer = lazy(
  () => import("./containers/opd-bills/AddOPDBillContainer"),
);
const EditOPDBillContainer = lazy(
  () => import("./containers/opd-bills/EditOPDBillContainer"),
);

const VisitPageContainer = lazy(
  () => import("./containers/cases/VisitPageContainer"),
);
const RoleManagementContainer = lazy(
  () => import("./containers/roleManagement/RoleManagementContainer"),
);

const ReportContainer = lazy(
  () => import("./containers/patient/ReportContainer"),
);

const HospitalReportsContainer = lazy(
  () => import("./containers/HospitalReports/HospitalReportsContainer"),
);

const HospitalReportConfigureContainer = lazy(
  () => import("./containers/HospitalReportConfigure/HospitalReportConfigureContainer"),
);

const DischargeSummary = lazy(
  () => import("./containers/discharge-summary/DischargeSummary"),
);

const DocumentDeficiency = lazy(
  () => import("./containers/document-deficiency/DocumentDeficiency"),
);

const SchemeOnboardingContainer = lazy(
  () => import("./containers/scheme-onboarding/SchemeOnboardingContainer"),
);

const VisitHistoryContainer = lazy(
  () => import("./containers/patient/VisitHistoryContainer"),
);

const G7NephroQueryListContainer = lazy(
  () => import("./containers/patient/Query/G7NephroQueryListContainer"),
);

const QueryListContainer = lazy(
  () => import("./containers/patient/Query/QueryListContainer"),
);

const NephroQueryListContainer = lazy(
  () => import("./containers/nephroPlus/NephroQueryListContainer"),
);

const PortalLoginContainer = lazy(
  () => import("./containers/TMS Portal Login/PortalLoginContainer"),
);

const NHAPortalLoginContainer = lazy(
  () => import("./containers/NHAPortalLogin/NHAPortalLoginCon"),
);

const ReconciliationContainer = lazy(
  () => import("./containers/patient/Reconciliation/ReconciliationContainer"),
);

const ReconciliationFileData = lazy(
  () => import("./components/patient/Reconciliation/ReconciliationFileData"),
);

const EODBODScreen = lazy(
  () => import("./containers/patient/EOD-BOD-Screen/EODBODScreenContainer"),
);

const ManagementAlertContainer = lazy(
  () => import("./containers/ManagementAlert/ManagementAlertContainer"),
);

const AgeStageWiseContainer = lazy(
  () => import("./containers/Age&StageWise/AgeStageWiseContainer"),
);

const HospitalManagementDashboardContainer = lazy(
  () =>
    import("./containers/hospital-management-dashboard/HospitalManagementDashboardContainer"),
);

const HospitalOperationalDashboardContainer = lazy(
  () =>
    import("./containers/hospital-operational-dashboard/HospitalOperationalDashboardContainer"),
);

const HospitalDeskDashboardContainer = lazy(
  () =>
    import("./containers/hospital-desk-dashboard/HospitalDeskDashboardContainer"),
);

const AllPortalLoginContainer = lazy(
  () => import("./containers/Login All Portal/AllPortalLoginContainer"),
);

const OHDRListContainer = lazy(
  () => import("./containers/ohdr/OHDRListContainer"),
);

const OHDRDetailsContainer = lazy(
  () => import("./containers/ohdr/OHDRDetailsContainer"),
);

const OHDRPatientRegistrationContainer = lazy(
  () => import("./containers/ohdr/OHDRPatientRegistrationContainer"),
);

const OTCListContainer = lazy(
  () => import("./containers/nephroPlus/OTCListContainer"),
);

const OPReconciliationContainer = lazy(
  () => import("./containers/patient/Reconciliation/OPReconciliationContainer"),
);

const CreditRegisterUploadContainer = lazy(
  () =>
    import("./containers/creditRegisterUpload/CreditRegisterUploadContainer"),
);

const PortalDataFetchContainer = lazy(
  () => import("./containers/portalDataFetch/PortalDataFetchContainer")
);

const IPReconciliationContainer = lazy(
  () => import("./containers/patient/Reconciliation/IPReconciliationContainer"),
);

const AssignedUserContainer = lazy(
  () => import("./containers/userManagement/AssignedUserContainer"),
);

const UploadFileFolderContainer = lazy(
  () => import("./containers/UploadFileFolder/UploadFileFolderContainer"),
);

const RegistersDataContainer = lazy(
  () => import("./containers/RegistersData/RegistersDataContainer"),
);

const BillAuditAuthBridge = lazy(
  () => import("./pages/BillAuditAuthBridge"),
);

const BillAuditContainer = lazy(
  () => import("./containers/BillAudit/BillAuditContainer"),
);

const OpCasesContainer = lazy(
  () => import("./containers/OpCases/OpCasesContainer"),
);

const OpCasesDocumentsContainer = lazy(
  () => import("./containers/OpCases/OpCasesDocumentsContainer"),
);

const CghsDisallowanceContainer = lazy(
  () => import("./containers/Disallowance/CghsDisallowanceContainer"),
);

const EchsDisallowanceContainer = lazy(
  () => import("./containers/Disallowance/EchsDisallowanceContainer"),
);
const UTRReconciliationContainer = lazy(
  () => import("./containers/UTRReconciliation/UTRReconciliationContainer"),
);

const routes: RouteObject[] = [
  {
    element: <Outlet />,
    errorElement: <RouteErrorFallback />,
    children: [
  {
    path: ROUTES.BILL_AUDIT_AUTH_BRIDGE,
    element: (
      <Suspense fallback={null}>
        <BillAuditAuthBridge />
      </Suspense>
    ),
  },
  {
    path: ROUTES.LANDING,
    loader: loginLoader,
    element: (
      <Suspense fallback={<Loader />}>
        <AuthContainer />
      </Suspense>
    ),
  },
  {
    path: ROUTES.LOGIN,
    loader: loginLoader,
    element: (
      <Suspense fallback={<Loader />}>
        <AuthContainer />
      </Suspense>
    ),
  },
  {
    path: ROUTES.LANDING_PAGE,
    element: (
      <Suspense fallback={<Loader />}>
        <LandingPageContainer />
      </Suspense>
    ),
    loader: authLoader,
    children: [
      {
        index: true,
        path: ROUTES.CLAIMS_LIST,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="claimStatus">
            <Suspense fallback={<Loader />}>
              <ClaimsListContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.MAILS,
        element: (
          <Suspense fallback={<Loader />}>
            <EmailsContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DASHBOARD,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="claimStatus">
            <Suspense fallback={<Loader />}>
              <DashboardContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.MANAGEMENT_DASHBOARD,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="claimStatus">
            <Suspense fallback={<Loader />}>
              <NewDashboardContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.HOSPITAL_MANAGEMENT_DASHBOARD,
        element: (
          <Suspense fallback={<Loader />}>
            <HospitalManagementDashboardContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.HOSPITAL_OPERATIONAL_DASHBOARD,
        element: (
          <Suspense fallback={<Loader />}>
            <HospitalOperationalDashboardContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.HOSPITAL_DESK_DASHBOARD,
        element: (
          <Suspense fallback={<Loader />}>
            <HospitalDeskDashboardContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.OPERATIONAL_DASHBOARD,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="claimStatus">
          <Suspense fallback={<Loader />}>
            <OperationalDashboard />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.PATIENT_LIST,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="patientList">
            <Suspense fallback={<Loader />}>
              {/* <PatientListContainer /> */}
              <PatientListContainerTabs />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.QUERY_LIST,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="patientList">
          <Suspense fallback={<Loader />}>
            {/* <QueryListContainer /> */}
            <G7NephroQueryListContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.NEPHRO_QUERY_LIST,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="nephroQueryList">
          <Suspense fallback={<Loader />}>
            {/* <NephroQueryListContainer /> */}
            <G7NephroQueryListContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.CASE_LIST,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="caseList">
            <Suspense fallback={<Loader />}>
              <CaseListContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.RECONCILIATION,
        element: (
          <Suspense fallback={<Loader />}>
            <ReconciliationContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.RECONCILIATION_FILE_DATA}/:id`,
        element: (
          <Suspense fallback={<Loader />}>
            <ReconciliationFileData />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TMS_PORTAL_LOGIN,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="cghsPortalLogin">
            <Suspense fallback={<Loader />}>
              <PortalLoginContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.TMS_PORTAL_LOGIN,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="cghsPortalLogin">
            <Suspense fallback={<Loader />}>
              <PortalLoginContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.NHA_PORTAL_LOGIN,
        element: (
          <Suspense fallback={<Loader />}>
            <NHAPortalLoginContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.ALL_PORTAL_LOGIN,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="cghsPortalLogin">
            <Suspense fallback={<Loader />}>
              <AllPortalLoginContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.CREDIT_REGISTER_UPLOAD,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="creditRegisterUpload">
          <Suspense fallback={<Loader />}>
            <CreditRegisterUploadContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.CREDIT_REGISTER_UPLOAD}/:id/:type`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="creditRegisterUpload">
          <Suspense fallback={<Loader />}>
            <CreditRegisterFileData />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.REGISTERS_DATA}`,
        element: (
          <Suspense fallback={<Loader />}>
            <RegistersDataContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.ALL_VISIT}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="patientAdd">
            <Suspense fallback={<Loader />}>
              <VisitHistoryContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.ADD_PATIENT,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="patientAdd">
            <Suspense fallback={<Loader />}>
              <AddPatientContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ADD_PATIENT_SCHEME}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="schemeAdd">
            <Suspense fallback={<Loader />}>
              <AddPatientSchemeContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ADD_PATIENT_SCHEME}/:id/:schemeId`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="schemeAdd">
            <Suspense fallback={<Loader />}>
              <AddPatientSchemeContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ALL_VISIT}/:id`, //ROUTES.PATIENT_DETAILS
        element: (
          <FunctionalityBasedRoute requiredFunctionality="patientList">
            <Suspense fallback={<Loader />}>
              <PatientDetailsContainer flag={false} />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.PATIENT_ADD}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="caseAdd">
            <Suspense fallback={<Loader />}>
              <PatientAddContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.CASE_DETAILS}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="caseList">
            <Suspense fallback={<Loader />}>
              <CaseDetailsContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.PATIENT_CASE_DETAILS}/:id/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="caseList">
            <Suspense fallback={<Loader />}>
              <PatientCaseDetailsContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.CASE_HISTORY}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="caseList">
            <Suspense fallback={<Loader />}>
              <CaseHistoryContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.VISIT_HISTORY}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="visitAdd">
            <Suspense fallback={<Loader />}>
              <VisitListContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.VISIT_DETAILS}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="visits">
            <Suspense fallback={<Loader />}>
              <VisitPageContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.BASIC_DETAILS}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="casePreAuthView">
            <Suspense fallback={<Loader />}>
              <PreAuthContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.REPORTS}`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="reports">
          <Suspense fallback={<Loader />}>
            <ReportContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.HOSPITAL_REPORTS}`,
        element: (
          <Suspense fallback={<Loader />}>
            <HospitalReportsContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.HOSPITAL_REPORTS_CONFIG}`,
        element: (
          <Suspense fallback={<Loader />}>
            <HospitalReportConfigureContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.DISCHARGE_SUMMARY}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="dischargeSummary">
            <Suspense fallback={<Loader />}>
              <DischargeSummary />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.DOCUMENT_DEFICIENCY}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="documentDeficiency">
            <Suspense fallback={<Loader />}>
              <DocumentDeficiency />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.DOCUMENT_DEFICIENCY}/:id/:visitId/:schemeId`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="documentDeficiency">
            <Suspense fallback={<Loader />}>
              <DocumentDeficiencyPerPatient />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.USER_MANAGEMENT}`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="userManagement">
          <Suspense fallback={<Loader />}>
            <UserManagementContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.NEW_USER_MANAGEMENT}`,
        element: (
          <Suspense fallback={<Loader />}>
            <NewUserManagementContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TASK_ALLOCATION}`,
        element: (
          <Suspense fallback={<Loader />}>
            <TaskAllocationContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.NEW_ALLOCATION}`,
        element: (
          <Suspense fallback={<Loader />}>
            <CreateOrEditTaskAllocationContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.ASSIGNED_USER_LIST}`,
        element: (
          <Suspense fallback={<Loader />}>
            <AssignedUserContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.SCHEME_ONBOARDING}`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="documentDeficiency">
          <Suspense fallback={<Loader />}>
            <SchemeOnboardingContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ADD_HEALTHCARE}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="healthCareList">
            <Suspense fallback={<Loader />}>
              <HealthCareContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.HEALTHCARE_LIST}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="healthCareList">
            <Suspense fallback={<Loader />}>
              <HealthCareListContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.HEALTHCARE_DETAILS}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="healthCareList">
            <Suspense fallback={<Loader />}>
              <HealthCareContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.INSURANCE_SETUP}`,
        element: (
          <Suspense fallback={<Loader />}>
            <InsuranceSetupContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.ALL_CASES}`,
        element: (
          <Suspense fallback={<Loader />}>
            <AllCasesContainer />
          </Suspense>
        ),
      },

      //Patient Billing
      {
        path: `${ROUTES.PATIENT_BILLING_LIST}/:caseId/:visitId`,
        element: (
          <Suspense fallback={<Loader />}>
            <PatientBillingListContainer />
          </Suspense>
        ),
      },
      //Common Billing Routes
      {
        path: `${ROUTES.BILLS_LIST}`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="billsList">
          <Suspense fallback={<Loader />}>
            <BillsContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ADD_NEW_BILL}`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="addNewBill">
          <Suspense fallback={<Loader />}>
            <AddBillContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.TPA_BILL_ITEMS_REVIEW}`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="tpaBillItemsReview">
          <Suspense fallback={<Loader />}>
            <BillItemsReviewContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.GOVT_BILL_ITEMS_REVIEW}`,
        element: (
          <Suspense fallback={<Loader />}>
            <GovtBillItemsReviewContainer />
          </Suspense>
        ),
      },

      {
        path: `${ROUTES.TPA_BILL_AUDIT}/:visitId/:tpaBillMappingId`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="tpaBillAudit">
          <Suspense fallback={<Loader />}>
            <TPABillAuditContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.GOVT_BILL_AUDIT}/:visitId/:govtBillMappingId`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="govtBillAudit">
          <Suspense fallback={<Loader />}>
            <GovtBillAuditContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      // {
      //   path: `${ROUTES.DS_BILL_COMP}/:visitId/:govtBillMappingId`,
      //   element: (
      //     // <FunctionalityBasedRoute requiredFunctionality="DSBillComparison">
      //     <Suspense fallback={<Loader />}>
      //       <DSBillCompContainer />
      //     </Suspense>
      //     // </FunctionalityBasedRoute>
      //   ),
      // },
      // IPD Billing routes
      {
        path: `${ROUTES.IPD_BILLING}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="ipdBillList">
            <Suspense fallback={<Loader />}>
              <IPDBillingContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ADD_NEW_IPD_BILL}/:id`,
        element: (
          <Suspense fallback={<Loader />}>
            <AddNewIPDBillContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.ADD_NEW_IPD_BILL,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="ipdBillList">
            <Suspense fallback={<Loader />}>
              <AddNewIPDBillContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.EDIT_IDP_BILL}/:ipdBillId`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="ipdBillList">
            <Suspense fallback={<Loader />}>
              <EditIPDBillContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      // OPD Bill Routes
      {
        path: `${ROUTES.OPD_BILL_LIST}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="opdBillList">
            <Suspense fallback={<Loader />}>
              <OPDBillingContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ADD_NEW_OPD_BILL}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="opdBillList">
            <Suspense fallback={<Loader />}>
              <AddNewOPDBillContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ADD_NEW_OPD_BILL}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="opdBillList">
            <Suspense fallback={<Loader />}>
              <AddNewOPDBillContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.EDIT_OPD_BILL}/:id`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="opdBillList">
            <Suspense fallback={<Loader />}>
              <EditOPDBillContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.ROLE_MANAGEMENT}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="roleManagement">
            <Suspense fallback={<Loader />}>
              <RoleManagementContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.EMAIL_LIST}`,
        element: (
          <FunctionalityBasedRoute requiredFunctionality="patientList">
            <Suspense fallback={<Loader />}>
              <EmailContainer />
            </Suspense>
          </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.APPOINTMENT_LIST,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="ohdrList">
          <Suspense fallback={<Loader />}>
            <OHDRListContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: `${ROUTES.OHDR_DETAILS}/:id`,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="ohdrDetails">
          <Suspense fallback={<Loader />}>
            <OHDRDetailsContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.OHDR_PATIENT_REGISTRATION,
        element: (
          <Suspense fallback={<Loader />}>
            <OHDRPatientRegistrationContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.HDR_LIST,
        element: (
          <Suspense fallback={<Loader />}>
            <HDRListContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.OTC_LIST,
        element: (
          // <FunctionalityBasedRoute requiredFunctionality="otcList">
          <Suspense fallback={<Loader />}>
            <OTCListContainer />
          </Suspense>
          // </FunctionalityBasedRoute>
        ),
      },
      {
        path: ROUTES.OP_RECONCILIATION,
        element: (
          <Suspense fallback={<Loader />}>
            <OPReconciliationContainer />
          </Suspense>
        ),
      },
      // {
      //   path: ROUTES.IP_RECONCILIATION,
      //   element: (
      //     <Suspense fallback={<Loader />}>
      //       <IPReconciliationContainer />
      //     </Suspense>
      //   ),
      // },
      {
        path: ROUTES.PORTAL_DETAILS_FETCH,
        element: (
          <Suspense fallback={<Loader />}>
            <PortalDetailsFetchContainer/>
          </Suspense>
        ),
      },
      {
        path: ROUTES.IP_RECONCILIATION,
        element: (
          <Suspense fallback={<Loader />}>
            <IPReconciliationContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PORTAL_DETAILS_FETCH,
        element: (
          <Suspense fallback={<Loader />}>
            <PortalDetailsFetchContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PORTAL_DATA_FETCH,
        element: (
          <Suspense fallback={<Loader />}>
            <PortalDataFetchContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.EOD_BOD,
        element: (
          <Suspense fallback={<Loader />}>
            <EODBODScreenContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.MANAGEMENT_ALERT,
        element: (
          <Suspense fallback={<Loader />}>
            <ManagementAlertContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.DOCUMENTS_UPLOAD}`,
        element: (
          <Suspense fallback={<Loader />}>
            <UploadFileFolderContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.AGE_STAGE_WISE,
        element: (
          <Suspense fallback={<Loader />}>
            <AgeStageWiseContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.BILL_AUDIT,
        element: (
          <Suspense fallback={<Loader />}>
            <BillAuditContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.OP_CASES,
        element: (
          <Suspense fallback={<Loader />}>
            <OpCasesContainer />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.OP_CASES_DOCUMENTS}/:visitId/:billMappingId?`,
        element: (
          <Suspense fallback={<Loader />}>
            <OpCasesDocumentsContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.CGHS_DISALLOWANCE,
        element: (
          <Suspense fallback={<Loader />}>
            <CghsDisallowanceContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.ECHS_DISALLOWANCE,
        element: (
          <Suspense fallback={<Loader />}>
            <EchsDisallowanceContainer />
          </Suspense>
        ),
      },
      {
        path: ROUTES.UTR_RECONCILIATION,
        element: (
          <Suspense fallback={<Loader />}>
            < UTRReconciliationContainer />
          </Suspense>
        ),
      },
    ],
  },
    ],
  },
];

const router = createBrowserRouter(routes);

function App() {
  useEffect(() => {
    syncSessionCookieFromStorage();
    return setupBillAuditTokenBridge();
  }, []);

  return (
    <div className="scale-container">
      <div className="scaled-content">
        <Suspense fallback={<Loader />}>
          <RouterProvider router={router} />
        </Suspense>
      </div>
    </div>
  );
}

export default App;
